import { createElement, useEffect, useState, type ReactNode } from "react";
import type { BindingCtx, DomainRead } from "../../../state/editor/types";
import type { InertTag, SectionDef } from "../inspector/schema";
import type { GltfAnimationOptions, SceneLoopEntry, SceneLoopState, VirtualCameraState } from "../../../shared/api-contract";
import { CompactCheck, CompactNumber, CompactSelect, CompactSlider, CompactText, MiniBtn, Row } from "../inspector/rows";
import { Icon } from "../../kit";
import { useCaps } from "../CapsProvider";
import { useViewer } from "../../../state/ViewerProvider";

/** `Row` requires `children` in props, so pass it explicitly. */
const row = (label: string, child: ReactNode, inert?: InertTag) => createElement(Row, { label, inert, children: child });
import { AnimationPanel } from "../animations/AnimationPanel";
import { GltfAnimationControl } from "./custom/GltfAnimationControl";
import { CameraViewsControl } from "./custom/CameraViewsControl";
import { InteractionPromptControl } from "./custom/InteractionPromptControl";
import { PhysicsControl, ScrollCameraControl } from "./custom/NichePluginControls";

/**
 * Animations tab. Nine sections:
 *  - GLTF Clips: baked AnimationMixer clips from the loaded model (v15), live
 *    over the `animationStateChanged` event.
 *  - Camera Views: saved viewpoints + tour / turntable (v15) + the
 *    ScrollableCameraView LIVE driver (v32).
 *  - Interaction Prompt: the viewer-side "drag to rotate" hint (v15).
 *  - Scrollable Camera View Preview / Physics: niche plugins (v25/v26).
 *  - Virtual Cameras: named saved poses (v32, distinct from Camera Views'
 *    fly-to-and-tour list — a simpler activate-by-name registry).
 *  - Scene Loop: the kiosk auto-cycling playlist (v32).
 *  - Pose Plugin: PCA auto-orient + drop-to-ground (checklist placement —
 *    moved here from Modifiers' old "Auto Pose").
 *  - Keyframe Timeline: the host-authored timeline editor (ported AnimationMenu,
 *    self-contained — owns presets/tracks/keyframes/stagger/video export).
 */

/** seekOnScroll (`CameraViewsConfig.seekOnScroll`; stored/round-trips via
 * setCameraViewsConfig but CameraViewsControl.tsx renders no row for it). */
function SeekOnScrollRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const r = ctx.ring;
  const [v, setV] = useState(false);
  useEffect(() => {
    r.getCameraViewsConfig().then((c) => setV(c.seekOnScroll)).catch(() => {});
  }, [r]);
  return row(
    "Seek On Scroll",
    createElement(CompactCheck, {
      checked: v,
      onChange: (next: boolean) => {
        setV(next);
        void r.setCameraViewsConfig({ seekOnScroll: next });
      },
    }),
  );
}

const DEFAULT_GLTF_DRAG_SCROLL = {
  autoIncrementTime: true,
  dragAxis: "y" as "x" | "y",
  dragAnimationDamping: 0.3,
  scrollAnimationDamping: 0.1,
};

/**
 * GLTF clip drag/scroll scrubbing options (webgi, v32) — extends the existing
 * `animateOnDrag`/`animateOnScroll` toggles already in `GltfAnimationControl`
 * with the axis/damping/auto-advance knobs. Rides the existing
 * `setGltfAnimationOptions`/`getGltfPlaybackState` (no new method name), so no
 * caps gate is required — a pre-v32 viewer's response just omits these
 * fields, which the defaults below cover.
 */
function GltfDragScrollRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const r = ctx.ring;
  const [p, setP] = useState(DEFAULT_GLTF_DRAG_SCROLL);
  useEffect(() => {
    r.getGltfPlaybackState().then((s) => setP({
      autoIncrementTime: s.autoIncrementTime ?? true,
      dragAxis: s.dragAxis ?? "y",
      dragAnimationDamping: s.dragAnimationDamping ?? 0.3,
      scrollAnimationDamping: s.scrollAnimationDamping ?? 0.1,
    })).catch(() => {});
  }, [r]);
  const set = (patch: GltfAnimationOptions) => {
    setP((prev) => ({ ...prev, ...patch }));
    void r.setGltfAnimationOptions(patch);
  };
  return createElement(
    "div",
    { className: "flex flex-col" },
    row("Auto Increment Time", createElement(CompactCheck, { checked: p.autoIncrementTime, onChange: (v: boolean) => set({ autoIncrementTime: v }) })),
    row("Drag Axis", createElement(CompactSelect, {
      value: p.dragAxis,
      options: [{ id: "x", label: "X" }, { id: "y", label: "Y" }],
      onChange: (v: string) => set({ dragAxis: v as "x" | "y" }),
    })),
    row("Drag Damping", createElement(CompactSlider, { value: p.dragAnimationDamping, min: 0, max: 1, step: 0.01, onChange: (v: number) => set({ dragAnimationDamping: v }) })),
    row("Scroll Damping", createElement(CompactSlider, { value: p.scrollAnimationDamping, min: 0, max: 1, step: 0.01, onChange: (v: number) => set({ scrollAnimationDamping: v }) })),
  );
}

/**
 * ScrollableCameraView LIVE driver (v32) — distinct from the "Scrollable
 * Camera View Preview (Dev)" niche plugin below (that one drives an
 * inertial orbit; this one places the camera at fraction t along the SAVED
 * Camera Views path, and emits a working page-scroll embed snippet). New
 * method names ⇒ gated behind caps.v32.
 */
function ScrollProgressRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const caps = useCaps();
  const r = ctx.ring;
  const [t, setT] = useState(0);
  const [code, setCode] = useState("");
  if (!caps.v32) return null;
  return createElement(
    "div",
    { className: "flex flex-col" },
    row("Scroll Progress", createElement(CompactSlider, { value: t, min: 0, max: 1, step: 0.001, onChange: (v: number) => { setT(v); void r.setScrollProgress(v); } })),
    createElement(
      "div",
      { className: "flex flex-wrap gap-1.5 px-3 py-1.5" },
      createElement(MiniBtn, { icon: "code", onClick: () => void r.generateScrollViewsCode().then(setCode), children: "Generate Embed Code" }),
    ),
    code
      ? createElement("pre", { className: "mx-3 mb-2 max-h-32 overflow-auto rounded bg-ed-panel-2 p-2 text-[11px] text-ed-text-2 whitespace-pre-wrap" }, code)
      : null,
  );
}

/**
 * Virtual Cameras (v32, `VirtualCamerasPlugin`) — a named-pose registry:
 * capture the live camera under a name, activate any saved one by name,
 * update/remove/clear. New method names ⇒ gated behind caps.v32.
 */
function VirtualCamerasRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const caps = useCaps();
  const r = ctx.ring;
  const [cams, setCams] = useState<VirtualCameraState[]>([]);
  const [name, setName] = useState("");
  const refresh = () => { r.listVirtualCameras().then(setCams).catch(() => {}); };
  useEffect(() => { if (caps.v32) refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [r, caps.v32]);
  if (!caps.v32) {
    return createElement("span", { className: "block px-3 py-1.5 text-[11px] text-ed-text-3" }, "Needs viewer contract v32 or newer.");
  }
  const add = () => {
    const n = name.trim();
    if (!n) return;
    void r.addVirtualCamera(n).then(() => { setName(""); refresh(); });
  };
  return createElement(
    "div",
    { className: "flex flex-col gap-1.5 px-3 py-1.5" },
    createElement(
      "div",
      { className: "flex min-w-0 flex-1 gap-1" },
      createElement(CompactText, { value: name, placeholder: "Camera name…", onChange: setName }),
      createElement(MiniBtn, { icon: "add_a_photo", primary: true, onClick: add, children: "Capture" }),
    ),
    cams.length === 0
      ? createElement("span", { className: "text-[11px] text-ed-text-3" }, "No saved virtual cameras.")
      : createElement(
          "ul",
          { className: "flex flex-col gap-1" },
          ...cams.map((c) =>
            createElement(
              "li",
              { key: c.name, className: "flex items-center gap-1.5 rounded border border-ed-border bg-white px-2 py-1" },
              createElement(
                "button",
                { className: "flex min-w-0 flex-1 items-center gap-1.5 text-left", onClick: () => void r.activateVirtualCamera(c.name, { animate: true }), title: "Activate" },
                createElement(Icon, { name: "videocam", size: 14, className: "shrink-0 text-ed-text-3" }),
                createElement("span", { className: "truncate text-[12px] text-ed-text" }, c.name),
              ),
              createElement("button", {
                className: "shrink-0 text-ed-text-3 hover:text-ed-accent",
                title: "Update to current view",
                // Omitted pose ⇒ the engine captures the live camera and
                // overwrites the existing name (VirtualCameras.add).
                onClick: () => void r.addVirtualCamera(c.name).then(refresh),
              }, createElement(Icon, { name: "sync", size: 14 })),
              createElement("button", {
                className: "shrink-0 text-ed-text-3 hover:text-red-500",
                title: "Remove",
                onClick: () => void r.removeVirtualCamera(c.name).then(refresh),
              }, createElement(Icon, { name: "close", size: 14 })),
            ),
          ),
        ),
    cams.length > 0
      ? createElement(MiniBtn, { icon: "delete_sweep", onClick: () => void r.clearVirtualCameras().then(refresh), children: "Clear All" })
      : null,
  );
}

const EMPTY_SCENE_LOOP: SceneLoopState = { playing: false, intervalMs: 4000, index: -1, count: 0, entries: [] };

/**
 * Scene Loop / kiosk playlist (v32) — cycles a list of model URLs / scene
 * presets on a timer, unattended-display style. Stays live over the
 * `sceneLoopStateChanged` event. New method names ⇒ gated behind caps.v32.
 */
function SceneLoopRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const caps = useCaps();
  const { client } = useViewer();
  const r = ctx.ring;
  const [s, setS] = useState<SceneLoopState>(EMPTY_SCENE_LOOP);
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!caps.v32) return;
    r.getSceneLoopState().then(setS).catch(() => {});
    const off = client.on("sceneLoopStateChanged", (p) => setS(p as SceneLoopState));
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r, client, caps.v32]);
  if (!caps.v32) {
    return createElement("span", { className: "block px-3 py-1.5 text-[11px] text-ed-text-3" }, "Needs viewer contract v32 or newer.");
  }
  const setEntries = (entries: SceneLoopEntry[]) => void r.setSceneLoopPlaylist(entries, { intervalMs: s.intervalMs }).then(setS);
  const addEntry = () => {
    const u = url.trim();
    if (!u) return;
    setUrl("");
    setEntries([...s.entries, { kind: "model", url: u }]);
  };
  const removeEntry = (i: number) => setEntries(s.entries.filter((_, idx) => idx !== i));
  return createElement(
    "div",
    { className: "flex flex-col gap-1.5 px-3 py-1.5" },
    row("Interval (ms)", createElement(CompactNumber, { value: s.intervalMs, min: 250, step: 250, width: 80, onChange: (v: number) => void r.setSceneLoopInterval(v).then(setS) })),
    createElement(
      "div",
      { className: "flex min-w-0 flex-1 gap-1" },
      createElement(CompactText, { value: url, placeholder: "Model URL…", onChange: setUrl }),
      createElement(MiniBtn, { icon: "add", onClick: addEntry, children: "Add" }),
    ),
    s.entries.length === 0
      ? createElement("span", { className: "text-[11px] text-ed-text-3" }, "Empty playlist — add a model URL above.")
      : createElement(
          "ul",
          { className: "flex flex-col gap-1" },
          ...s.entries.map((e, i) =>
            createElement(
              "li",
              { key: i, className: `flex items-center gap-1.5 rounded border px-2 py-1 ${i === s.index ? "border-ed-accent bg-ed-active" : "border-ed-border bg-white"}` },
              createElement("span", { className: "min-w-0 flex-1 truncate text-[12px] text-ed-text" }, e.kind === "model" ? e.url : `preset: ${e.name}`),
              createElement("button", { className: "shrink-0 text-ed-text-3 hover:text-red-500", onClick: () => removeEntry(i) }, createElement(Icon, { name: "close", size: 14 })),
            ),
          ),
        ),
    createElement(
      "div",
      { className: "flex flex-wrap gap-1.5" },
      s.playing
        ? createElement(MiniBtn, { icon: "stop", onClick: () => void r.stopSceneLoop().then(setS), children: "Stop" })
        : createElement(MiniBtn, { icon: "play_arrow", primary: true, onClick: () => void r.startSceneLoop().then(setS), children: "Start" }),
      createElement(MiniBtn, { icon: "skip_next", onClick: () => void r.nextSceneLoopItem().then(setS), children: "Next" }),
    ),
    createElement("span", { className: "text-[11px] tabular-nums text-ed-text-3" }, `${s.playing ? "playing" : "stopped"} · ${s.count ? s.index + 1 : 0}/${s.count}`),
  );
}

export const animationsSections: SectionDef[] = [
  {
    id: "gltf-clips",
    title: "GLTF Animations",
    rows: [
      { id: "gltf", kind: "custom", label: "", component: () => createElement(GltfAnimationControl) },
      // Checklist "Reset" button (rewinds the mixer to t=0). GltfAnimationControl
      // only exposes Play/Pause/Stop; stopGltfAnimation already rewinds, so this
      // sibling row covers the missing layout item without touching that file.
      {
        id: "reset",
        kind: "button",
        label: "",
        buttons: [{ label: "Reset", icon: "restart_alt", run: (ctx) => ctx.ring.stopGltfAnimation() }],
      },
      // Drag/scroll scrubbing options (webgi, v32) not covered by
      // GltfAnimationControl's existing animateOnDrag/OnScroll toggles.
      { id: "dragScroll", kind: "custom", label: "", keywords: "Drag Scroll", component: GltfDragScrollRow },
    ],
  },
  {
    id: "camera-views",
    title: "Camera Views",
    rows: [
      { id: "views", kind: "custom", label: "", component: () => createElement(CameraViewsControl) },
      { id: "seekOnScroll", kind: "custom", label: "", keywords: "Seek on Scroll", component: SeekOnScrollRow },
      // ScrollableCameraView LIVE driver (v32): places the camera along the
      // saved-views path at a scroll fraction + emits an embed snippet.
      { id: "scrollProgress", kind: "custom", label: "", keywords: "Scroll Progress", component: ScrollProgressRow },
    ],
  },
  {
    id: "interaction-prompt",
    title: "Interaction Prompt",
    defaultCollapsed: true,
    rows: [{ id: "prompt", kind: "custom", label: "", component: () => createElement(InteractionPromptControl) }],
  },
  {
    id: "scrollCamera",
    title: "Scrollable Camera View Preview (Dev)",
    inert: {
      label: "dev only",
      reason:
        "A code-generator preview for an embedding host, not a viewer feature; the parity docs flag it dev-only and it duplicates the live ScrollableCameraView driver above.",
    },
    defaultCollapsed: true,
    rows: [{ id: "scroll", kind: "custom", label: "", component: () => createElement(ScrollCameraControl) }],
  },
  {
    id: "physics",
    title: "Physics",
    defaultCollapsed: true,
    rows: [{ id: "physics", kind: "custom", label: "", component: () => createElement(PhysicsControl) }],
  },
  {
    // Named saved poses (v32) — a simple activate-by-name registry, distinct
    // from the Camera Views tour list above.
    id: "virtualCameras",
    title: "Virtual Cameras",
    defaultCollapsed: true,
    rows: [{ id: "vcams", kind: "custom", label: "", component: VirtualCamerasRow }],
  },
  {
    // Kiosk auto-cycling playlist (v32, `SceneLoop`).
    id: "sceneLoop",
    title: "Scene Loop (Kiosk)",
    defaultCollapsed: true,
    rows: [{ id: "loop", kind: "custom", label: "", component: SceneLoopRow }],
  },
  {
    // Checklist verbatim title (was "Auto Pose" in the Modifiers tab).
    id: "posePlugin",
    title: "Pose Plugin",
    defaultCollapsed: true,
    rows: [
      {
        id: "pose",
        kind: "button",
        label: "",
        buttons: [
          { label: "Compute Pose", icon: "compass_calibration", variant: "primary", run: (ctx) => ctx.ring.computePose() },
          { label: "Drop to Ground", icon: "vertical_align_bottom", run: (ctx) => ctx.ring.computePose({ orient: false, dropToGround: true }) },
        ],
      },
      { id: "poseNote", kind: "note", label: "", text: "PCA auto-orients the model (longest axis horizontal) and rests it on the ground plane." },
    ],
  },
  {
    id: "timeline",
    title: "Keyframe Timeline",
    defaultCollapsed: true,
    rows: [{ id: "panel", kind: "custom", label: "", component: () => createElement(AnimationPanel) }],
  },
];
